// Platform adapter for handing the person a file (rep-app program, spec
// section 2 "share.ts": Web Share with a blob fallback to <a download>; never
// window.open on an authenticated URL, which would carry no Bearer token).
// W9 swaps the body for @capacitor/share behind the same call.
export type ShareFile = { blob: Blob; filename: string; title?: string };
export type ShareResult = 'shared' | 'downloaded' | 'cancelled';
type Deps = { navigator: Navigator; document: Document };

const REVOKE_AFTER_MS = 60_000;

export async function shareFile({ blob, filename, title }: ShareFile, deps: Deps = { navigator: globalThis.navigator, document: globalThis.document }): Promise<ShareResult> {
  const nav = deps.navigator as Navigator & { canShare?: (data: ShareData) => boolean };
  const file = typeof File !== 'undefined' ? new File([blob], filename, { type: blob.type }) : null;
  if (file && typeof nav.share === 'function' && typeof nav.canShare === 'function' && nav.canShare({ files: [file] })) {
    try {
      await nav.share({ files: [file], title: title ?? filename });
      return 'shared';
    } catch (err) {
      if ((err as { name?: string }).name === 'AbortError') return 'cancelled';
      // Sharing failed for another reason: fall through to the download.
    }
  }
  const url = URL.createObjectURL(blob);
  const a = deps.document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  a.style.display = 'none';
  deps.document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), REVOKE_AFTER_MS);
  return 'downloaded';
}

// Handing the person a few lines of text (the Sales app's item page: name,
// code, price): the share sheet where the device has one, else the
// clipboard. W9 swaps the body for @capacitor/share behind the same call.
export type ShareTextResult = 'shared' | 'copied' | 'cancelled' | 'unavailable';

export async function shareText({ title, text }: { title: string; text: string }, deps: { navigator: Navigator | undefined } = { navigator: globalThis.navigator }): Promise<ShareTextResult> {
  const nav = deps.navigator;
  if (nav && typeof nav.share === 'function') {
    try {
      await nav.share({ title, text });
      return 'shared';
    } catch (err) {
      if ((err as { name?: string }).name === 'AbortError') return 'cancelled';
      // Sharing failed for another reason: fall through to the clipboard.
    }
  }
  if (nav?.clipboard && typeof nav.clipboard.writeText === 'function') {
    try {
      await nav.clipboard.writeText(text);
      return 'copied';
    } catch {
      // clipboard refused
    }
  }
  return 'unavailable';
}
